import React, { useState, useEffect, useCallback } from 'react';
import { listSharesFromCloud, acceptShareInCloud, declineShareInCloud } from '../utils/cloudApi';

const POLL_MS = 60000;

/**
 * IncomingSharesNotice — banner + review modal for surveys shared to this
 * account from other accounts.
 *
 * Self-contained: polls GET /shares?direction=incoming, shows a fixed
 * banner while pending shares exist, and opens a review modal where each
 * share can be accepted (creates a draft copy in this account) or declined.
 *
 * @param {Function} onAccepted - Called after a share is accepted, so the
 *                                console can refresh Ready-to-deploy.
 * @param {Function} onError    - Error reporter (App-level setError).
 */
export default function IncomingSharesNotice({ onAccepted, onError }) {
    const [pending, setPending] = useState([]);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const shares = await listSharesFromCloud({ direction: 'incoming' });
            setPending((shares || []).filter(s => s.status === 'pending'));
        } catch {
            // Polling failure is not worth a banner; try again next tick.
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    const act = async (share, action) => {
        setBusyId(share.share_id);
        try {
            if (action === 'accept') {
                await acceptShareInCloud({ shareId: share.share_id });
                onAccepted?.();
            } else {
                await declineShareInCloud({ shareId: share.share_id });
            }
            await load();
        } catch (e) {
            onError?.(e.message);
        } finally {
            setBusyId(null);
        }
    };

    if (pending.length === 0) return null;

    const buttonBase = {
        padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', border: '1px solid #DADCE0', background: '#fff', color: '#3C4043',
    };

    return (
        <>
            {/* Banner */}
            <div style={{
                position: 'fixed', left: 12, bottom: 12, zIndex: 9000,
                background: '#E8F0FE', border: '1px solid #B8D4FF', borderRadius: 8,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: 13, color: '#1967D2',
            }}>
                <span>
                    {pending.length === 1
                        ? 'A survey has been shared with your account.'
                        : `${pending.length} surveys have been shared with your account.`}
                </span>
                <button onClick={() => setReviewOpen(true)} style={{ ...buttonBase, background: '#1967D2', border: 'none', color: '#fff' }}>
                    Review
                </button>
            </div>

            {/* Review modal */}
            {reviewOpen && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 10000,
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={() => setReviewOpen(false)}>
                    <div style={{
                        background: '#fff', borderRadius: 8, padding: 24, maxWidth: 520, width: '90%',
                        maxHeight: '70vh', overflow: 'auto',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#3C4043' }}>Shared surveys</h3>
                        <div style={{ margin: '0 0 16px', fontSize: 13, color: '#5F6368' }}>
                            Accepting creates a draft copy in your account. Declining discards the share; the sender's original is unaffected either way.
                        </div>
                        {pending.map(share => (
                            <div key={share.share_id} style={{
                                border: '1px solid #E8EAED', borderRadius: 6, padding: 12, marginBottom: 10,
                            }}>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#202124' }}>{share.title}</div>
                                <div style={{ fontSize: 12, color: '#5F6368', marginTop: 2 }}>
                                    From {share.from_email || share.from_org_name || 'another account'}
                                    {' · '}{share.created_at ? new Date(share.created_at).toLocaleDateString() : ''}
                                </div>
                                {share.message && (
                                    <div style={{ fontSize: 13, color: '#3C4043', marginTop: 6, fontStyle: 'italic' }}>
                                        “{share.message}”
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                                    <button
                                        disabled={busyId === share.share_id}
                                        onClick={() => act(share, 'decline')}
                                        style={buttonBase}
                                    >Decline</button>
                                    <button
                                        disabled={busyId === share.share_id}
                                        onClick={() => act(share, 'accept')}
                                        style={{ ...buttonBase, background: '#1967D2', border: 'none', color: '#fff' }}
                                    >Accept</button>
                                </div>
                            </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                            <button onClick={() => setReviewOpen(false)} style={buttonBase}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
